import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { redirectTo, routes } from 'utils/routing';
import { isFrame } from 'utils/next';
import { isFetchedStates, isFetchingStates } from 'store/utils';

import { CreatingOrderSelectors, EventsSelectors } from 'store/selectors';
import {
  AddonsActions,
  CreatingOrderActions,
  DonationsActions,
  EventsActions,
  PaymentMethodsActions,
  TicketsActions,
} from 'store/actions';

import withRouteValidation from 'hocs/withRouteValidation';
import withGetInitialPropsRouteValidation from 'hocs/withGetInitialPropsRouteValidation';

import EventTicketsScreen from 'components/screens/EventTicketsScreen';
import { TabTitle } from 'components/common/TabTitle';
import CreatingOrderLayout from 'components/creatingOrder/CreatingOrderLayout';
import RenderComponent from 'components/common/RenderComponent';
import { RootState } from 'store/rootReducer';
import { Theme, useMediaQuery } from '@mui/material';

const querySchema = Yup.object({
  eventId: Yup.number().required(),
});

const TicketsPage: NextPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();

  const eventId = Number(router.query.eventId);
  const admin = router.query.admin === String(true);
  const frame = router.query.frame === String(true) || isFrame();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

  const eventState = useSelector((state: RootState) => EventsSelectors.eventStateById(state, eventId));
  const paymentSystem = eventState.entity?.payment_system;
  const gateWayType = eventState.entity?.gateway_type;
  const hasTicketsCount = useSelector(CreatingOrderSelectors.hasTicketsCount);
  const totalTicketsCount = useSelector(CreatingOrderSelectors.getTotalTicketsCount);
  const thirdPartyLink = eventState.entity?.settings?.third_party_link;

  useEffect(() => {
    dispatch(CreatingOrderActions.resetCreatingOrder());
    dispatch(PaymentMethodsActions.resetTempPaymentMethod());
    dispatch(
      CreatingOrderActions.initCart({
        isRegisteringByAdmin: admin,
        event: {
          event: eventId,
          answers: [],
        },
        gateWayType,
      }),
    );
  }, [dispatch, eventId, admin, gateWayType, paymentSystem]);

  useEffect(() => {
    if (thirdPartyLink) {
      window.location.replace(thirdPartyLink);
    }
  }, [thirdPartyLink]);

  useEffect(() => {
    const route = routes.eventRegistration(eventId);
    router.prefetch(route.link);
  }, [router, eventId]);

  return (
    <div style={{ height: isMobile ? 'initial' : '100%' }}>
      <RenderComponent
        isFetched={isFetchedStates(eventState)}
        renderContent={() => (
          <TabTitle ns="public" i18nKey="tab.eventTickets" signLabel="general" event={eventState.entity.title} />
        )}
      />
      <CreatingOrderLayout frame={frame} isLoading={isFetchingStates(eventState)}>
        <EventTicketsScreen
          frame={frame}
          hasTicketsCount={hasTicketsCount}
          totalTicketsCount={totalTicketsCount}
          event={eventState.entity}
        />
      </CreatingOrderLayout>
    </div>
  );
};

// todo change to getServerSide
TicketsPage.getInitialProps = withGetInitialPropsRouteValidation(querySchema, async ({ store, res, query }: any) => {
  const eventId = Number(query.eventId);

  if (eventId) {
    store.dispatch(EventsActions.getEvent.request({ id: eventId }));
    store.dispatch(DonationsActions.getEventDonation.request({ id: eventId }));
    store.dispatch(TicketsActions.getEventTickets.request({ params: { eventId } }));
    store.dispatch(AddonsActions.getEventAddons.request({ params: { eventId } }));

    return {};
  }

  return redirectTo(res, routes.event(eventId));
});

export default withRouteValidation(TicketsPage);
